/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */

import React, { useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableWithoutFeedback,
  Keyboard,
  Dimensions,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import Input from '@components/form/Input';
import SociollaText from '@components/sociolla/SociollaText';
import Button from '@components/form/Button';
import ModalFull from '@components/modal/ModalFull';

import { showPopup } from '@/store/modal';
import { currency } from '@global/helpers';
import { isWeb, isMobileWeb } from '@global/screens';
import Colors from '@global/colors';

const dimension = Dimensions.get('screen');

const TITLE_ACTUAL_CLAIM =
  'Editing actual claim on SKP External will affect budget used and remaining budget on SKP Internal';

const BudgetRow = React.memo(({ item, rowIndex, handleChange }) => {
  const [localValue, setLocalValue] = useState(item.budget_sample || '');

  const handleLocalChange = useCallback(
    (value) => {
      setLocalValue(value);
      handleChange(value, item);
    },
    [handleChange, item],
  );

  return (
    <View
      testID={`skp-external-actual-claim-modal-row-${rowIndex}`}
      style={[styles.row, rowIndex % 2 === 0 ? styles.evenRow : styles.oddRow]}>
      <View style={[styles.cell, styles.brandColumn]}>
        <SociollaText style={styles.brandText} numberOfLines={0}>
          {item.brand?.name || ''}
        </SociollaText>
      </View>
      <View style={[styles.cell, styles.actualClaimColumn]}>
        <Input
          value={localValue}
          onChangeText={handleLocalChange}
          placeholder="Enter actual claim"
          keyboardType="numeric"
          isNumeric
          style={styles.budgetInput}
          editable={true}
        />
      </View>
      <View style={[styles.cell, styles.dppColumn]}>
        <SociollaText style={styles.dppText}>
          {currency(item.dpp, true)}
        </SociollaText>
      </View>
    </View>
  );
});

BudgetRow.displayName = 'BudgetRow';

const BudgetTable = ({ budgets, handleChange }) => (
  <View style={styles.tableContainer}>
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <View style={[styles.headerCell, styles.brandColumn]}>
          <SociollaText style={styles.headerText}>Brand</SociollaText>
        </View>
        <View style={[styles.headerCell, styles.actualClaimColumn]}>
          <SociollaText style={styles.headerText}>Actual Claim</SociollaText>
        </View>
        <View style={[styles.headerCell, styles.dppColumn]}>
          <SociollaText style={styles.headerText}>DPP Actual</SociollaText>
        </View>
      </View>
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={isWeb}
        keyboardShouldPersistTaps="handled">
        {budgets.map((item, rowIndex) => (
          <BudgetRow
            key={item.brand?.id || rowIndex}
            item={item}
            rowIndex={rowIndex}
            handleChange={handleChange}
          />
        ))}
      </ScrollView>
    </View>
  </View>
);

const SkpExternalBudgetTableModal = ({
  data = [],
  changeActualClaim,
  computeDpp,
}) => {
  const dispatch = useDispatch();
  const editActualClaim = useSelector((state) => state.modal.editActualClaim);

  const [budgetValues, setBudgetValues] = useState(() =>
    JSON.parse(JSON.stringify(data)),
  );

  const handleBudgetChange = useCallback(
    (value, item) => {
      setBudgetValues((prev) =>
        prev.map((p) => {
          if (p.brand.id !== item.brand.id) {
            return p;
          }
          const updated = { ...p, budget_sample: value };
          if (typeof computeDpp === 'function') {
            updated.dpp = computeDpp(updated);
          }
          return updated;
        }),
      );
    },
    [computeDpp],
  );

  const handleUpdateBudget = () => {
    changeActualClaim(budgetValues);
    dispatch(showPopup({ name: 'editActualClaim', value: false }));
  };

  const onClose = () => {
    setBudgetValues([]);
    dispatch(showPopup({ name: 'editActualClaim', value: false }));
  };

  const renderContent = () => (
    <View style={{ flex: 1 }}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ flexGrow: 1 }}>
        <SociollaText type="regular" style={styles.title}>
          {TITLE_ACTUAL_CLAIM}
        </SociollaText>
        <BudgetTable budgets={budgetValues} handleChange={handleBudgetChange} />
        <View style={styles.modalContainer}>
          <Button
            testID="skp-external-budget-modal-cancel-btn"
            rounded
            type="primary"
            buttonStyle={{
              width: isWeb
                ? 100
                : dimension.width / 2 - (isMobileWeb ? 20 : 25),
              backgroundColor: Colors.white,
            }}
            disabledStyle={{ width: 90 }}
            title={
              <SociollaText type="medium" style={styles.cancelButtonText}>
                Cancel
              </SociollaText>
            }
            onPress={onClose}
          />
          <Button
            testID="skp-external-budget-modal-save-btn"
            rounded
            type="primary"
            buttonStyle={{
              width: isWeb
                ? 100
                : dimension.width / 2 - (isMobileWeb ? 20 : 25),
              marginLeft: 10,
            }}
            disabledStyle={{ width: 90 }}
            title={
              <SociollaText type="medium" style={styles.saveButtonText}>
                Save
              </SociollaText>
            }
            onPress={handleUpdateBudget}
          />
        </View>
      </ScrollView>
    </View>
  );

  return (
    <ModalFull
      testID="skp-external-actual-claim-modal"
      show={editActualClaim}
      title="Edit Actual Claim"
      width={isWeb ? 780 : '100%'}
      onClose={onClose}>
      {isWeb || isMobileWeb ? (
        renderContent()
      ) : (
        <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
          {renderContent()}
        </TouchableWithoutFeedback>
      )}
    </ModalFull>
  );
};

const styles = StyleSheet.create({
  title: {
    fontSize: 14,
    color: Colors.black,
    marginLeft: 15,
    marginTop: isWeb ? 0 : 20,
    marginBottom: isWeb ? 0 : 20,
  },
  cancelButtonText: {
    fontSize: 14,
    color: Colors.black,
  },
  saveButtonText: {
    fontSize: 14,
    color: Colors.white,
  },
  modalContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingVertical: isWeb ? 20 : 15,
    paddingHorizontal: isWeb ? 20 : 15,
    backgroundColor: Colors.white,
    ...(!isWeb
      ? {
          position: isMobileWeb ? 'fixed' : 'absolute',
          bottom: 0,
          right: 0,
          left: 0,
        }
      : {}),
  },
  tableContainer: {
    paddingHorizontal: 10,
    paddingVertical: isWeb ? 10 : 0,
    flex: 1,
  },
  container: {
    borderWidth: 1,
    borderColor: Colors.grey18,
    overflow: 'hidden',
    ...(isWeb && {
      width: '100%',
      alignSelf: 'center',
    }),
  },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: Colors.blue3,
  },
  headerCell: {
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: Colors.grey9,
    minHeight: 50,
  },
  headerText: {
    fontWeight: 'bold',
    fontSize: 14,
    textAlign: 'center',
    color: Colors.white,
  },
  scrollView: {
    maxHeight: 400,
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey9,
    minHeight: 60,
  },
  evenRow: {
    backgroundColor: Colors.white,
  },
  oddRow: {
    backgroundColor: Colors.grey4,
  },
  cell: {
    padding: 12,
    justifyContent: 'center',
    borderRightWidth: 1,
    borderRightColor: Colors.grey9,
  },
  brandColumn: {
    flex: 1,
  },
  actualClaimColumn: {
    flex: 1,
  },
  dppColumn: {
    flex: 1,
  },
  brandText: {
    lineHeight: 20,
    flexWrap: 'wrap',
    textAlign: 'left',
  },
  dppText: {
    textAlign: 'center',
  },
  budgetInput: {
    ...(isMobileWeb
      ? {
          width: 80,
        }
      : {}),
  },
});

export default SkpExternalBudgetTableModal;
